import type { BasePluginOptions, IElementEvent, RuntimeContext, TooltipOptions } from '@antv/g6'
import { BasePlugin, Tooltip } from '@antv/g6'
import * as ReactDom from 'react-dom/client'
import { ReactRenderTooltip } from '../components/tooltip'

interface CustomTooltipOptions extends BasePluginOptions {}

export class CustomTooltip extends BasePlugin<CustomTooltipOptions> {
  private root: ReactDom.Root | null = null
  private tooltipPosition: { x: number; y: number } = { x: 0, y: 0 }
  private currentTarget: string | null = null

  constructor(context: RuntimeContext, options: TooltipOptions) {
    console.log('constructor', options)
    super(context, Object.assign({}, Tooltip.defaultOptions, options))
    this.render()
    this.bindEvents()
  }

  private getEvents(): { [key: string]: (event: IElementEvent) => void } {
    return {
      'node:pointerover': this.onPointerOver,
      'node:pointermove': this.onPointerMove,
      'node:drag': this.onPointerLeave,
      'edge:pointerover': this.onPointerLeave,
      'edge:pointermove': this.onPointerLeave,
      'combo:pointerover': this.onPointerLeave,
      'combo:pointermove': this.onPointerLeave,
      'canvas:pointermove': this.onCanvasMove,
      contextmenu: this.onPointerLeave,
    }
  }

  private bindEvents() {
    const { graph } = this.context
    const events = this.getEvents()
    Object.keys(events).forEach(eventName => {
      graph.on(eventName, events[eventName])
    })
  }

  private unbindEvents() {
    const { graph } = this.context
    const events = this.getEvents()
    Object.keys(events).forEach(eventName => {
      graph.off(eventName, events[eventName])
    })
  }

  private onPointerOver = (event: IElementEvent) => {
    this.show(event)
  }

  public onPointerMove = (event: IElementEvent) => {
    const { target } = event
    if (!this.currentTarget || target.id === this.currentTarget) {
      return
    }
    this.show(event)
  }

  public onPointerLeave = (event: IElementEvent) => {
    this.hide(event)
  }

  public onCanvasMove = (event: IElementEvent) => {
    this.hide(event)
  }

  private get tooltipStyleProps() {
    const { canvas } = this.context
    const { center } = canvas.getBounds()
    const $container = canvas.getContainer() as HTMLElement
    const { top, left } = $container.getBoundingClientRect()
    const { style, position, enterable, container = { x: -left, y: -top }, title, offset } = this.options
    const [x, y] = center
    const [width, height] = canvas.getSize()

    return {
      x,
      y,
      container,
      title,
      bounding: { x: 0, y: 0, width, height },
      position,
      enterable,
      offset,
      style,
    }
  }

  private initTooltip = () => {
    const container = document.createElement('div')
    const root = ReactDom.createRoot(container)
    document.body.appendChild(container)
    return root
  }

  public show = async (event: IElementEvent) => {
    const {
      client,
      target: { id },
    } = event

    this.currentTarget = id
    this.tooltipPosition = { x: client.x, y: client.y }
    if (this.root) {
      const nodeData = this.context.graph.getNodeData(id)
      console.log('show', event, nodeData)

      this.root.render(
        <div style={{ ...this.tooltipStyleProps, position: 'absolute', left: this.tooltipPosition.x + 10, top: this.tooltipPosition.y }}>
          <ReactRenderTooltip text={id} name={nodeData?.name || ''} />
        </div>,
      )
    }
  }

  public update(_options: any = {}) {}

  public hide = (_event?: IElementEvent) => {
    if (this.root) {
      this.root.render(<div style={{ ...this.tooltipStyleProps, position: 'absolute', left: 0, top: 0 }}></div>)
    }
  }

  private render() {
    const { canvas } = this.context
    const $container = canvas.getContainer()
    if (!$container) return
    this.root = this.initTooltip()
  }

  public destroy(): void {
    this.unbindEvents()
    if (this.root) {
      this.root.unmount()
    }
    super.destroy()
  }
}
